"use client";

import { Button } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { deriveFftStep } from "@/components/fft/derive-fft-step";
import { useAnalytics } from "@/contexts/analytics-context";
import { useClaimAllRewards } from "@/contexts/mutations/campaigns/use-claim-all-rewards";
import { useClaimCampaignReward } from "@/contexts/mutations/campaigns/use-claim-campaign-reward";
import { useActiveTrialQuery } from "@/contexts/queries/funded-trial/active-trial";
import { useFundedTrialEligibilityQuery } from "@/contexts/queries/funded-trial/eligibility";
import { useRewardsList } from "@/contexts/queries/rewards/use-rewards-list";
import {
  type RewardsSummary,
  useRewardsSummary,
} from "@/contexts/queries/rewards/use-rewards-summary";
import { useAuthState } from "@/hooks/use-auth-state";
import { getServerTime } from "@/utils/server-time";

import { FILTER_TRANSLATION_KEYS } from "./category-keys";
import { HeadBanner } from "./components/banner/head-banner";
import { LifetimeEarnedCard } from "./components/banner/lifetime-earned-card";
import { ReadyToClaimCard } from "./components/banner/ready-to-claim-card";
import { WeeklyTrendCard } from "./components/banner/weekly-trend-card";
import { YtdCard } from "./components/banner/ytd-card";
import { ConnectGate } from "./components/connect-gate";
import { RewardsHeader } from "./components/header";
import { FftRewardCard, FftRewardCardSkeleton } from "./components/reward-card/fft-reward-card";
import { RewardCard } from "./components/reward-card/reward-card";
import { RewardsTabs } from "./components/tabs/rewards-tabs";
import { RewardsSkeleton } from "./skeleton";
import type { FftCardData, HubCard, Reward, RewardsFilter, RewardsList } from "./types";

function deriveFftCard({
  eligibility,
  activeTrial,
}: {
  eligibility: { data?: FftCardData["eligibility"]; isError: boolean };
  activeTrial: { data?: FftCardData["activeTrial"]; isError: boolean };
}): FftCardData | "loading" | null {
  if (eligibility.data !== undefined && activeTrial.data !== undefined) {
    return {
      step: deriveFftStep({
        eligibility: eligibility.data,
        activeTrial: activeTrial.data ?? null,
      }),
      eligibility: eligibility.data,
      activeTrial: activeTrial.data ?? null,
    };
  }

  if (!eligibility.isError && !activeTrial.isError) {
    return "loading";
  }

  return null;
}

export function RewardsContent() {
  const { state, accountAddress } = useAuthState();
  const list = useRewardsList(accountAddress);
  const summary = useRewardsSummary(accountAddress);
  const fftEligibility = useFundedTrialEligibilityQuery({ account: accountAddress });
  const fftActiveTrial = useActiveTrialQuery({ account: accountAddress });

  if (state === "LOADING") {
    return <RewardsSkeleton />;
  }
  if (state === "DISCONNECTED") {
    return (
      <RewardsShell>
        <ConnectGate />
      </RewardsShell>
    );
  }
  if (list.isError || summary.isError) {
    return (
      <RewardsShell>
        <ErrorGate onRetry={() => void list.refetch()} />
      </RewardsShell>
    );
  }
  if (list.isLoading || summary.isLoading || !summary.data) {
    return <RewardsSkeleton />;
  }

  const fftCard = deriveFftCard({
    eligibility: fftEligibility,
    activeTrial: fftActiveTrial,
  });

  return (
    <RewardsShell>
      <RewardsContentLoaded
        list={list.data}
        summary={summary.data}
        fftCard={fftCard}
        onRefetchList={() => void list.refetch()}
      />
    </RewardsShell>
  );
}

function RewardsShell({ children }: { children: React.ReactNode }) {
  return (
    <div className={stack({ gap: "32" })}>
      <RewardsHeader />
      {children}
    </div>
  );
}

interface RewardsContentLoadedProps {
  list: RewardsList;
  summary: RewardsSummary;
  fftCard: FftCardData | "loading" | null;
  onRefetchList: () => void;
}

function RewardsContentLoaded({
  list,
  summary,
  fftCard,
  onRefetchList,
}: RewardsContentLoadedProps) {
  const [filter, setFilter] = useState<RewardsFilter>("all");
  const [claimingCampaignId, setClaimingCampaignId] = useState<number | null>(null);
  const { mutateAsync: claim } = useClaimCampaignReward();
  const { mutateAsync: claimAll, isPending: isClaimingAll } = useClaimAllRewards();
  const analytics = useAnalytics();

  const claimableRewards = useMemo(
    () => list.rewards.filter((r) => r.status === "claimable"),
    [list.rewards],
  );

  const filteredRewards = useMemo(
    () => (filter === "all" ? list.rewards : list.rewards.filter((r) => r.status === filter)),
    [filter, list.rewards],
  );

  // FFT has no RewardStatus — always shown at index 0, filter-bypassed.
  const hubCards = useMemo<HubCard[]>(() => {
    const campaignCards = filteredRewards.map((r) => ({ kind: "campaign" as const, ...r }));
    if (fftCard === "loading") {
      return [{ kind: "fft-skeleton" }, ...campaignCards];
    }
    return fftCard ? [{ kind: "fft", ...fftCard }, ...campaignCards] : campaignCards;
  }, [filteredRewards, fftCard]);

  const handleClaimOne = async (reward: Reward) => {
    setClaimingCampaignId(reward.id);
    try {
      await claim({
        source: "card",
        campaignId: reward.id,
        category: reward.category,
        rewardAmountUsd: reward.rewardAmountUsd,
      });
    } catch {
      // onError toasts
    } finally {
      setClaimingCampaignId(null);
    }
  };

  const handleClaimAll = async () => {
    // Atomic batch aborts on a single expired row — pre-filter stale claim windows.
    const nowSec = Math.floor(getServerTime() / 1000);
    const rewards = claimableRewards.filter((r) => nowSec <= r.claimEndTsSec);
    if (rewards.length === 0) {
      onRefetchList();
      return;
    }
    try {
      await claimAll(rewards);
    } catch {
      // swallow rejection — onError owns the toast
    }
  };

  const isAnyClaimInFlight = isClaimingAll || claimingCampaignId !== null;

  const handleTabChange = (next: RewardsFilter) => {
    analytics.rewards.tabChanged({ from: filter, to: next });
    setFilter(next);
  };

  return (
    <>
      <HeadBanner
        lifetimeSlot={
          <LifetimeEarnedCard
            totalEarnedUsd={summary.lifetimeEarnedUsd}
            totalClaimedUsd={summary.totalClaimedUsd}
            breakdown={summary.breakdownByType}
          />
        }
        readyToClaimSlot={
          <ReadyToClaimCard
            count={claimableRewards.length}
            totalUsd={summary.readyToClaimUsd}
            onClaimAll={() => void handleClaimAll()}
            isClaimingAll={isClaimingAll}
            disabled={isAnyClaimInFlight}
          />
        }
        ytdSlot={
          <YtdCard yearToDateUsd={summary.yearToDateUsd} weeklyWowPct={summary.weeklyWowPct} />
        }
        weeklyTrendSlot={<WeeklyTrendCard weeklyBreakdown={summary.weeklyBreakdown} />}
      />
      <div className={stack({ gap: "16" })}>
        <RewardsTabs value={filter} onChange={handleTabChange} counts={list.counts} />
        <RewardGrid
          cards={hubCards}
          filter={filter}
          onClaim={(reward) => void handleClaimOne(reward)}
          claimingCampaignId={claimingCampaignId}
          anyClaimInFlight={isAnyClaimInFlight}
        />
      </div>
    </>
  );
}

interface RewardGridProps {
  cards: HubCard[];
  filter: RewardsFilter;
  onClaim: (reward: Reward) => void;
  claimingCampaignId: number | null;
  anyClaimInFlight: boolean;
}

function RewardGrid({
  cards,
  filter,
  onClaim,
  claimingCampaignId,
  anyClaimInFlight,
}: RewardGridProps) {
  if (cards.length === 0) {
    return <EmptyState filter={filter} />;
  }

  return (
    <div className={gridStyles}>
      {cards.map((card) => {
        switch (card.kind) {
          case "fft-skeleton":
            return <FftRewardCardSkeleton key="fft" />;
          case "fft":
            return <FftRewardCard key="fft" card={card} />;
          case "campaign":
            return (
              <RewardCard
                key={card.id}
                reward={card}
                onClaim={onClaim}
                claimingCampaignId={claimingCampaignId}
                anyClaimInFlight={anyClaimInFlight}
              />
            );
        }
      })}
    </div>
  );
}

const gridStyles = css({
  display: "grid",
  gap: "12",
  gridTemplateColumns: {
    base: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    desktop: "repeat(3, minmax(0, 1fr))",
  },
});

const placeholderStyles = css({
  py: "32",
  textAlign: "center",
  textStyle: "body.lg",
  color: "text.tertiary",
});

function EmptyState({ filter }: { filter: RewardsFilter }) {
  const t = useTranslations("rewards.emptyStates");
  return <div className={placeholderStyles}>{t(FILTER_TRANSLATION_KEYS[filter])}</div>;
}

function ErrorGate({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations("general");
  return (
    <div className={stack({ gap: "16", alignItems: "center", py: "32" })}>
      <p className={css({ textStyle: "body.lg", color: "text.tertiary", textAlign: "center" })}>
        {t("fetchError")}
      </p>
      <Button variant="primary" size="md" onClick={onRetry}>
        {t("tryAgain")}
      </Button>
    </div>
  );
}
